import { ART_HEIGHT, ART_WIDTH, BODY_LAYOUT, WING_SHAPES, isFreeformShape, type MeadowContourShapeId, type MeadowShapeId } from './layout';
import { smoothCutoutEdges } from './smooth-cutout';

export type ButterflyTextures = { wing: HTMLCanvasElement; body: HTMLCanvasElement };

function makeCanvas(): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = ART_WIDTH;
  canvas.height = ART_HEIGHT;
  return canvas;
}

function context(canvas: HTMLCanvasElement, willReadFrequently = false): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d', { willReadFrequently });
  if (!ctx) throw new Error('Canvas 2D недоступен');
  return ctx;
}

/** Both wings: the printed left contour and the same contour mirrored to the right. */
export function wingsPath(shape: MeadowContourShapeId): Path2D {
  const { fore, hind } = WING_SHAPES[shape];
  const result = new Path2D();
  const mirror = new DOMMatrix([-1, 0, 0, 1, ART_WIDTH, 0]);
  for (const d of [fore, hind]) {
    result.addPath(new Path2D(d));
    result.addPath(new Path2D(d), mirror);
  }
  return result;
}

export function bodyPath(): Path2D {
  const { head, thorax, abdomen } = BODY_LAYOUT;
  const path = new Path2D();
  path.arc(head.cx, head.cy, head.r, 0, Math.PI * 2);
  path.ellipse(thorax.cx, thorax.cy, thorax.rx, thorax.ry, 0, 0, Math.PI * 2);
  path.ellipse(abdomen.cx, abdomen.cy, abdomen.rx, abdomen.ry, 0, 0, Math.PI * 2);
  return path;
}

function masked(art: CanvasImageSource, mask: (ctx: CanvasRenderingContext2D) => void, willRead = false): HTMLCanvasElement {
  const out = makeCanvas();
  const ctx = context(out, willRead);
  ctx.drawImage(art, 0, 0, ART_WIDTH, ART_HEIGHT);
  const maskCanvas = makeCanvas();
  mask(context(maskCanvas));
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(maskCanvas, 0, 0);
  return out;
}

function averageColor(canvas: HTMLCanvasElement): string {
  const { data } = context(canvas, true).getImageData(470, 110, 84, 360);
  let r = 0, g = 0, b = 0, n = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) continue;
    r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
  }
  return n ? `rgb(${Math.round(r / n)},${Math.round(g / n)},${Math.round(b / n)})` : '#3a2a1e';
}

/** «Своя бабочка»: крылья — это непрозрачные пиксели art за вычетом области тела (оно идёт в свою текстуру). */
function freeformWings(art: CanvasImageSource): HTMLCanvasElement {
  const out = makeCanvas();
  const ctx = context(out, true);
  ctx.drawImage(art, 0, 0, ART_WIDTH, ART_HEIGHT);
  // Uploaded freeform art has a binary cutout mask. Smooth it here so existing
  // butterflies benefit too, without changing their stored art or interior.
  const image = ctx.getImageData(0, 0, ART_WIDTH, ART_HEIGHT);
  image.data.set(smoothCutoutEdges(image.data, ART_WIDTH, ART_HEIGHT));
  ctx.putImageData(image, 0, 0);
  ctx.globalCompositeOperation = 'destination-out';
  const body = bodyPath();
  ctx.fill(body);
  ctx.lineWidth = 8; // the printed body outline is not a wing
  ctx.lineJoin = 'round';
  ctx.stroke(body);
  return out;
}

/** Body columns on the sheet: the band the wing roots are cleaned in. */
const BODY_COLUMNS = { from: 470, to: 554 } as const;

/**
 * The wing texture is cut along x = 492, but the drawn body (and its outline ring) reaches past the 3D body
 * in places, mostly where the thorax meets the abdomen. Every opaque wing pixel that belongs to the body
 * takes the colour of the nearest wing pixel further out on the same row, so the wing simply continues
 * under the 3D body instead of showing a sliver of body colour and outline next to it. `body` is the
 * body coverage (alpha) of the same size; left half searches left, right half searches right.
 */
export function bleedWingsIntoBody(wing: Uint8ClampedArray, body: Uint8ClampedArray, width: number, height: number, columns: { from: number; to: number } = BODY_COLUMNS): Uint8ClampedArray {
  const out = new Uint8ClampedArray(wing);
  const mid = width / 2;
  for (let y = 0; y < height; y++) {
    for (let x = Math.max(0, columns.from); x < Math.min(width, columns.to); x++) {
      const i = (y * width + x) * 4;
      if (!body[i + 3] || !wing[i + 3]) continue;
      const step = x < mid ? -1 : 1;
      let sx = x + step;
      while (sx >= 0 && sx < width && body[(y * width + sx) * 4 + 3]) sx += step;
      const j = (y * width + sx) * 4;
      if (sx < 0 || sx >= width || !wing[j + 3]) { out[i + 3] = 0; continue; }
      out[i] = wing[j]; out[i + 1] = wing[j + 1]; out[i + 2] = wing[j + 2]; out[i + 3] = wing[j + 3];
    }
  }
  return out;
}

/** Applies `bleedWingsIntoBody` with the printed body and its outline ring as the body area. */
function hideBodyInWings(wing: HTMLCanvasElement): void {
  const ctx = context(wing, true);
  const bodyMask = makeCanvas();
  const mask = context(bodyMask, true);
  const body = bodyPath();
  mask.fill(body);
  mask.lineWidth = 8; // the outline ring is drawn outside the body silhouette
  mask.lineJoin = 'round';
  mask.stroke(body);
  const w = BODY_COLUMNS.to - BODY_COLUMNS.from;
  const wingData = ctx.getImageData(BODY_COLUMNS.from, 0, w, ART_HEIGHT);
  const bodyData = mask.getImageData(BODY_COLUMNS.from, 0, w, ART_HEIGHT);
  // Columns are relative to the cropped band; its middle is the body axis.
  wingData.data.set(bleedWingsIntoBody(wingData.data, bodyData.data, w, ART_HEIGHT, { from: 0, to: w }));
  ctx.putImageData(wingData, BODY_COLUMNS.from, 0);
}

/**
 * Splits the 1024×512 art into the wing texture (only the wings, with the printed
 * outline) and the body texture. The body texture is filled with the body's
 * average colour around the drawing, because the 3D body's sides sample beyond
 * the outline in the top-down projection.
 */
export function splitArt(art: CanvasImageSource, shape: MeadowShapeId): ButterflyTextures {
  const freeform = isFreeformShape(shape);
  const wing = freeform ? freeformWings(art) : masked(art, (ctx) => {
    const wings = wingsPath(shape);
    ctx.fill(wings);
    ctx.lineWidth = 10;
    ctx.lineJoin = 'round';
    ctx.stroke(wings);
  }, true);
  if (!freeform) hideBodyInWings(wing);
  const bodyOnly = masked(art, (ctx) => ctx.fill(bodyPath()), true);
  const body = makeCanvas();
  const ctx = context(body);
  ctx.fillStyle = averageColor(bodyOnly);
  ctx.fillRect(0, 0, ART_WIDTH, ART_HEIGHT);
  ctx.drawImage(bodyOnly, 0, 0);
  return { wing, body };
}
